/**
 * todoModel — the to-dos page's view of one to-do (the to-dos upgrade,
 * Claude Design handoff 2026-10-04: refactor/design-sync/todos-upgrade/).
 *
 * One adapter from the API row to everything the page draws: the status the
 * person sees (Open · Done · Skipped — never the old New / In Progress), the
 * due bucket, the FROM label, the team, the target an action goes to, who
 * assigned it, and the canonical `TodoListItem` the detail modal is built on.
 * Every lens (Focus · Table · Board), the Done rail and search read this one
 * shape, so they cannot disagree about a to-do.
 */
import type { TodoRow, TodoTarget, TodoTeam } from '../../../services/todosApi';
import type { TodoListItem } from '../../engine/standaloneTodos';
import { adaptTodoRow } from '../../engine/dataSources/engineTodosDataSource';
import { todoTypeDef, type TodoType } from '../../shared/todoTypes';
import { parseEngineDate } from '../../../utils/engineDate';
import { todoDueBucket, todoDueDays, type TodoDueBucket } from '../todoDue';
import { repeatLabel } from '../todoRepeat';

export type TodoStatus = 'open' | 'done' | 'skipped';

/** Where a to-do came from — automatic, read-only (decision 4 of the brief). */
export type FromKind = 'Deal' | 'Project' | 'Ticket' | 'EOS' | 'Rock' | 'Call' | 'Repeat' | 'Typed in' | 'Other';

export interface TodoFrom {
  kind: FromKind;
  /** The record's name, when there is one ("Oz Realty", "Ops L10"). */
  name: string | null;
  /** The record's own page (THE GATE), when it has one. */
  href: string | null;
}

export const TEAM_LABEL: Record<TodoTeam, string> = { ops: 'Ops', sales: 'Sales', marketing: 'Marketing', admin: 'Admin' };
export const TEAMS: TodoTeam[] = ['ops', 'sales', 'marketing', 'admin'];

export interface TodoItem {
  id: number;
  row: TodoRow;
  /** The canonical modal's shape (engine/TodoDetailModal). */
  item: TodoListItem;
  title: string;
  type: TodoType;
  status: TodoStatus;
  /** YYYY-MM-DD, or null. */
  due: string | null;
  suggested: boolean;
  pinned: boolean;
  team: TodoTeam | null;
  clientId: string | null;
  clientName: string | null;
  from: TodoFrom;
  target: TodoTarget | null;
  targetMissing: string | null;
  assigneeId: number | null;
  assigneeName: string | null;
  assignedById: number | null;
  assignedByName: string | null;
  createdAt: Date | null;
  doneAt: Date | null;
  skippedAt: Date | null;
  repeat: string | null;
  hasDraft: boolean;
}

function fromFor(row: TodoRow): TodoFrom {
  const st = String(row.source_type || '');
  const label = row.source_label && String(row.source_label).trim() ? String(row.source_label).trim() : null;
  const sid = row.source_id ?? '';
  switch (st) {
    case 'deal':
      return { kind: 'Deal', name: label ?? `Deal #${sid}`, href: sid ? `/sales/cards/${sid}` : null };
    case 'project':
      return { kind: 'Project', name: label ?? `Project #${sid}`, href: sid ? `/operations/projects/${sid}` : null };
    case 'ticket': {
      const tid = row.ticket_id ?? (Number(String(sid).replace(/^ticket:/, '')) || null);
      return { kind: 'Ticket', name: label ?? (tid ? `Ticket #${tid}` : 'Ticket'), href: tid ? `/operations/tickets/${tid}` : null };
    }
    case 'eos_l10':
      return { kind: 'EOS', name: label ?? 'L10 meeting', href: sid ? `/eos/meetings/${sid}` : null };
    case 'eos_issue':
      return { kind: 'EOS', name: label ?? 'Issue', href: sid ? `/eos/ids/${sid}` : null };
    case 'eos_rock':
    case 'eos_rock_milestone':
      return { kind: 'Rock', name: label ?? 'Rock', href: row.eos_rock_id ? `/eos/rocks/${row.eos_rock_id}` : null };
    case 'fathom':
      return { kind: 'Call', name: label ?? 'Recorded call', href: null };
    case 'recurring':
      return { kind: 'Repeat', name: repeatLabel(row.recurrence_type, row.recurrence_config), href: null };
    case '':
    case 'manual': {
      // A typed-in to-do that repeats says so — the design's From chip reads
      // "Repeat · Every week on Fri" rather than "Typed in".
      const repeat = repeatLabel(row.recurrence_type, row.recurrence_config);
      return repeat ? { kind: 'Repeat', name: repeat, href: null } : { kind: 'Typed in', name: null, href: null };
    }
    default:
      return { kind: 'Other', name: label, href: null };
  }
}

function dayOf(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw));
  if (m) return m[1];
  const d = parseEngineDate(raw);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function statusOf(row: Pick<TodoRow, 'state' | 'completed_at' | 'is_completed'>): TodoStatus {
  if (row.state === 'skipped') return 'skipped';
  if (row.completed_at || row.is_completed) return 'done';
  return 'open';
}

export function toTodoItem(row: TodoRow): TodoItem {
  const status = statusOf(row);
  const team = row.team && ['ops', 'sales', 'marketing', 'admin'].includes(row.team) ? (row.team as TodoTeam) : null;
  return {
    id: row.id,
    row,
    item: adaptTodoRow(row),
    title: row.title,
    type: todoTypeDef(row.task_type).value,
    status,
    due: dayOf(row.due_date),
    suggested: !!row.due_suggested && !!row.due_date,
    pinned: !!row.pinned_at,
    team,
    clientId: row.client_card_id ?? null,
    clientName: row.client_name ?? null,
    from: fromFor(row),
    target: row.target ?? null,
    targetMissing: row.target_missing ?? null,
    assigneeId: row.assigned_to ?? null,
    assigneeName: row.assigned_name ?? null,
    assignedById: row.assigned_by ?? null,
    assignedByName: row.assigned_by_name ?? null,
    createdAt: parseEngineDate(row.created_at),
    doneAt: status === 'done' ? parseEngineDate(row.completed_at) : null,
    skippedAt: status === 'skipped' ? parseEngineDate(row.skipped_at ?? null) : null,
    repeat: repeatLabel(row.recurrence_type, row.recurrence_config),
    hasDraft: !!row.has_draft,
  };
}

// ── Buckets ──────────────────────────────────────────────────────────────

/** The Focus sections, top to bottom. Pinned to-dos form Priority and are
 *  still ordered by their due date (Michael, 2026-10-04: "pinned to-dos should
 *  still be sorted by due date"). */
export type FocusBucket = 'priority' | TodoDueBucket;

export interface FocusBucketDef {
  id: FocusBucket;
  label: string;
  /** Diamond + count tone: off-track for late, paprika for attention. */
  tone: 'off' | 'paprika' | null;
  /** Always shown, with this line when empty. */
  emptyCopy?: string;
}

export const FOCUS_BUCKETS: FocusBucketDef[] = [
  { id: 'priority', label: 'Priority', tone: null, emptyCopy: 'Pin a to-do to keep it on top' },
  { id: 'overdue', label: 'Overdue', tone: 'off' },
  { id: 'none', label: 'Needs a date', tone: 'paprika' },
  { id: 'today', label: 'Today', tone: 'paprika' },
  { id: 'tomorrow', label: 'Tomorrow', tone: null },
  { id: 'this_week', label: 'This week', tone: null },
  { id: 'later', label: 'Later', tone: null },
];

export function dueBucketOf(t: Pick<TodoItem, 'due'>, now: Date = new Date()): TodoDueBucket {
  return todoDueBucket(t.due, now);
}

export function focusBucketOf(t: Pick<TodoItem, 'due' | 'pinned'>, now: Date = new Date()): FocusBucket {
  return t.pinned ? 'priority' : todoDueBucket(t.due, now);
}

/** Soonest first; no date last; ties by title so the order is stable. */
export function byDue(a: TodoItem, b: TodoItem): number {
  const ad = a.due ?? '9999-12-31';
  const bd = b.due ?? '9999-12-31';
  if (ad !== bd) return ad < bd ? -1 : 1;
  return a.title.localeCompare(b.title);
}

/** Days from today, for metric math. */
export function daysOut(t: Pick<TodoItem, 'due'>, now: Date = new Date()): number | null {
  return todoDueDays(t.due, now);
}

/** Monday 00:00 of the current week, local. */
export function weekStart(now: Date = new Date()): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - back);
  return d;
}

/** A SQLite-comparable UTC timestamp ('YYYY-MM-DD HH:MM:SS') for a Date. */
export function toSqlUtc(d: Date): string {
  return d.toISOString().replace('T', ' ').slice(0, 19);
}

// ── Actions ──────────────────────────────────────────────────────────────

/** Email / text / call to-dos complete when the message actually goes out —
 *  the server refuses a plain tick (Phase 7c's action-task contract). On those
 *  rows the checkbox is a Skip control, behind a warning (Michael, 2026-10-04:
 *  "replaced with a skip icon and a warning before approved skipping"). */
export function completesOnSend(type: TodoType): boolean {
  return type === 'email' || type === 'text' || type === 'phone';
}

/** The verb on an action row, in the design's short form, or null. */
export const ROW_VERB: Partial<Record<TodoType, string>> = {
  email: 'Compose email',
  text: 'Send text',
  phone: 'Call',
  schedule_call: 'Book call',
  schedule_build: 'Schedule build',
  create_proposal: 'Draft proposal',
  create_workflow: 'Build workflow',
  edit_workflow: 'Edit workflow',
  send_form: 'Send form',
  ai_action: 'Run AI action',
};

/** Why the verb cannot run right now, or null when it can. A coming-soon type
 *  says so; a known recipient without the channel says which is missing. */
export function verbBlockedReason(t: Pick<TodoItem, 'type' | 'targetMissing'>): string | null {
  if (!todoTypeDef(t.type).functional) return 'Coming soon';
  return t.targetMissing ?? null;
}
